import type {JSX} from 'react';
import {useState} from 'react';

import {Button, DatePicker, Select} from '@/components';
import {Filter} from '@/components/Filter';
import {
  getTransactionProcessorsOptions,
  getTransactionStatusOptions,
  getTransactionTypesOptions,
} from '@/constants/formatters';

import type {TestTransactionListProps} from './types';

type TestTransactionFiltersProps = Pick<
  TestTransactionListProps,
  | 'filterCount'
  | 'currentFilters'
  | 'filtersToApply'
  | 'clearFilters'
  | 'selectAppliedFilters'
  | 'handleApplyFilters'
  | 'selectCurrentFilters'
>;

// The retail list's filter bar (components/transactions/TransactionFilters)
// narrowed to the fields a test transaction has.
export default function TestTransactionFilters({
  filterCount,
  currentFilters,
  filtersToApply,
  clearFilters,
  selectAppliedFilters,
  handleApplyFilters,
  selectCurrentFilters,
}: TestTransactionFiltersProps): JSX.Element {
  const [showFilters, setShowFilters] = useState<boolean>(false);

  const fromDate = filtersToApply.fromDate
    ? new Date(filtersToApply.fromDate)
    : undefined;

  const toDate = filtersToApply.toDate
    ? new Date(filtersToApply.toDate)
    : undefined;

  const handleApplyFilter = (): void => {
    // Only close on success, so a rejected date range stays on screen.
    if (handleApplyFilters()) {
      setShowFilters(false);
    }
  };

  return (
    <div className="mt-5 w-full">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex items-center space-x-3">
          <Filter
            closeFilter={() => setShowFilters(false)}
            handleShowFilter={() => setShowFilters(!showFilters)}
            label="Filters"
            positionLeft={1}
            bodyClassName="w-full md:w-[600px]"
          >
            {showFilters && (
              <div className="p-4 space-y-4 max-w-4xl mx-auto w-full col-span-full">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col md:flex-row gap-2">
                    <DatePicker
                      label="From Date"
                      selectedDate={fromDate}
                      setSelectedDate={(date) =>
                        selectAppliedFilters('fromDate', date)
                      }
                      maxDate={toDate}
                      placeholderText="Select Date"
                    />
                    <DatePicker
                      label="To Date"
                      selectedDate={toDate}
                      setSelectedDate={(date) =>
                        selectAppliedFilters('toDate', date)
                      }
                      minDate={fromDate}
                      placeholderText="Select Date"
                    />
                  </div>

                  <div className="flex flex-col md:flex-row gap-2">
                    <Select
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        selectAppliedFilters('type', e.target.value)
                      }
                      value={filtersToApply.type}
                      name="type"
                      options={getTransactionTypesOptions()}
                      placeholder="Select"
                      label="Filter by Type"
                    />
                    <Select
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        selectAppliedFilters('processor', e.target.value)
                      }
                      value={filtersToApply.processor}
                      name="processor"
                      options={getTransactionProcessorsOptions()}
                      placeholder="Select"
                      label="Filter by Processor"
                    />
                  </div>
                </div>

                <div className="flex md:justify-end">
                  <Button
                    onClick={clearFilters}
                    colorScheme="cyan"
                    variant="ghost"
                  >
                    Clear
                  </Button>

                  <Button
                    onClick={handleApplyFilter}
                    colorScheme="cyan"
                    disabled={filterCount === 0}
                    className="ml-3"
                  >
                    Apply Filters {filterCount && ` (${filterCount})`}
                  </Button>
                </div>
              </div>
            )}
          </Filter>
        </div>

        <Select
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            selectCurrentFilters('status', e.target.value)
          }
          value={currentFilters.status}
          name="status"
          options={getTransactionStatusOptions()}
          placeholder="Filter by Status"
        />
      </div>
    </div>
  );
}
